/**
 * Notifications Module
 *
 * Handles display and management of notification banners and notices.
 */

import { StorageKeys } from "./config.js";

/** Severity levels for notices, from highest to lowest urgency. */
export type NoticeSeverity = "critical" | "warning" | "info";

/**
 * A single notice entry from the notices.json schema.
 * Schema: { severity, title, message, datetime (ISO 8601) }
 */
export interface Notice {
  readonly severity: NoticeSeverity;
  readonly title: string;
  readonly message: string;
  readonly datetime: string;
}

/**
 * The top-level structure of notices.json.
 * An empty `notices` array means no banner is shown.
 */
export interface NoticesJson {
  readonly notices: readonly Notice[];
}

/**
 * Shows the AWS notification banner with the specified severity level.
 *
 * @param awsNotificationBanner - The notification banner element.
 * @param severity - The severity level of the notification.
 * @param title - The title of the notification.
 * @param message - The message content of the notification.
 * @param timestamp - The timestamp of the notification. Defaults to now.
 * @returns void
 */
export function showAwsNotification(
  awsNotificationBanner: HTMLElement,
  severity: NoticeSeverity,
  title: string,
  message: string,
  timestamp: Date = new Date()
): void {
  if (!awsNotificationBanner) {
    return;
  }

  // Remove any existing severity classes
  awsNotificationBanner.classList.remove(
    "notification-banner--critical",
    "notification-banner--warning",
    "notification-banner--info"
  );

  // Add the appropriate severity class
  awsNotificationBanner.classList.add(`notification-banner--${severity}`);

  // Update the content
  const titleElement = awsNotificationBanner.querySelector(".notification-banner__title");
  const textElement = awsNotificationBanner.querySelector(".notification-banner__text");
  const timeElement = awsNotificationBanner.querySelector("time");

  if (titleElement) titleElement.textContent = title;
  if (textElement) textElement.textContent = message;

  if (timeElement) {
    const formattedDate = timestamp.toISOString();
    const readableDate = timestamp.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });

    timeElement.setAttribute("datetime", formattedDate);
    timeElement.textContent = readableDate;
  }

  // Show the banner
  awsNotificationBanner.classList.remove("notification-banner--hidden");
}

/**
 * Hides the AWS notification banner.
 *
 * @param awsNotificationBanner - The notification banner element.
 * @returns void
 */
export function hideAwsNotification(awsNotificationBanner: HTMLElement): void {
  if (!awsNotificationBanner) return;
  awsNotificationBanner.classList.add("notification-banner--hidden");
}

/**
 * Builds the dismissal key for a set of notices: the `datetime` values of every
 * notice, joined. Dismissal is scoped to this exact set, so editing notices.json
 * (e.g. adding a new notice) changes the key and re-surfaces the banner even for
 * users who dismissed a prior notice.
 */
function noticesDismissalKey(notices: NoticesJson["notices"]): string {
  return notices.map(({ datetime }) => datetime).join("|");
}

/**
 * Displays AWS notices from the notices data if they haven't been dismissed.
 *
 * @param noticesData - The notices data object containing an array of notices.
 * @param awsNotificationBanner - The notification banner element.
 * @returns void
 */
export function showNotices(noticesData: NoticesJson, awsNotificationBanner: HTMLElement): void {
  const notices = noticesData?.notices ?? [];
  const hasNotices = notices.length > 0;
  const wasDismissed =
    hasNotices &&
    localStorage.getItem(StorageKeys.NOTIFICATION_DISMISSED) === noticesDismissalKey(notices);
  const shouldShowNotification = hasNotices && !wasDismissed;

  if (shouldShowNotification) {
    notices.forEach(({ severity, title, message, datetime }) =>
      showAwsNotification(awsNotificationBanner, severity, title, message, new Date(datetime))
    );
  }
}

/**
 * Persists dismissal for the given notices, scoped to their content (see
 * {@link noticesDismissalKey}) so a future change to notices.json is not silently
 * suppressed by a stale dismissal.
 *
 * @param noticesData - The notices data the user just dismissed.
 * @returns void
 */
export function dismissNotices(noticesData: NoticesJson): void {
  localStorage.setItem(
    StorageKeys.NOTIFICATION_DISMISSED,
    noticesDismissalKey(noticesData?.notices ?? [])
  );
}
